import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { authService } from "@/services/authService";
import { useUserStore } from "@/store/userStore";

import { Logo } from "@/components/ui/Logo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { authApi } from "@/api/authApi";
import { ROUTES } from "@/constants/routes";

import { isValidEmail, normalize } from "@/lib/validateUser";

type FieldName = "email" | "password";
type FieldErrors = Partial<Record<FieldName, string>>;

const ERROR_TEXT = {
  emailRequired: "Введите email",
  emailInvalid: "Введите корректный email",
  passwordRequired: "Введите пароль",
  formHasErrors: "Проверьте поля формы",
  loginError: "Ошибка при входе",
} as const;

const errorId = (field: FieldName) => `${field}-error`;

export const LoginPage = () => {
  const navigate = useNavigate();

  const setUser = useUserStore((s) => s.setUser);
  const clearUser = useUserStore((s) => s.clearUser);
  const setAuthenticated = useUserStore((s) => s.setAuthenticated);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);

  // показываем подсветку/ошибки только после первой попытки submit
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const clientErrors = useMemo<FieldErrors>(() => {
    const errs: FieldErrors = {};

    const normalizedEmail = normalize(email);

    if (!normalizedEmail) errs.email = ERROR_TEXT.emailRequired;
    else if (!isValidEmail(normalizedEmail)) errs.email = ERROR_TEXT.emailInvalid;

    if (!password) errs.password = ERROR_TEXT.passwordRequired;

    return errs;
  }, [email, password]);

  const focusFirstError = (errors: FieldErrors) => {
    if (errors.email) emailRef.current?.focus();
    else if (errors.password) passwordRef.current?.focus();
  };

  const getDisplayedError = (field: FieldName): string | undefined => {
    if (!submitAttempted) return undefined;
    return clientErrors[field];
  };

  const hasErrorsAfterSubmit =
    submitAttempted && Object.keys(clientErrors).length > 0;

  const isSubmitDisabled = isLoading || hasErrorsAfterSubmit;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitAttempted(true);
    setFormError(null);

    if (Object.keys(clientErrors).length > 0) {
      focusFirstError(clientErrors);
      return;
    }

    setIsLoading(true);

    try {
      const normalizedEmail = normalize(email);

      await authService.login(normalizedEmail, password);

      setAuthenticated(true);
      const user = await authApi.me();
      setUser(user);

      toast.success("Вход выполнен успешно");
      navigate(ROUTES.dashboard);
    } catch (err) {
      setAuthenticated(false);
      clearUser();

      const message = err instanceof Error ? err.message : ERROR_TEXT.loginError;
      setFormError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const emailError = getDisplayedError("email");
  const passwordError = getDisplayedError("password");

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-6">
        <Logo />
        <h1 className="fs-700 font-bold text-neutral-100">Вход в аккаунт</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <Input
            ref={emailRef}
            type="email"
            placeholder="E-mail"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFormError(null);
            }}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? errorId("email") : undefined}
            disabled={isLoading}
          />
          {emailError && (
            <p id={errorId("email")} className="fs-200 text-danger">
              {emailError}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <PasswordInput
            ref={passwordRef}
            placeholder="Введите пароль"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFormError(null);
            }}
            aria-invalid={!!passwordError}
            aria-describedby={passwordError ? errorId("password") : undefined}
            disabled={isLoading}
          />
          {passwordError && (
            <p id={errorId("password")} className="fs-200 text-danger">
              {passwordError}
            </p>
          )}
        </div>

        <div className="flex flex-col items-center gap-4">
          <Button type="submit" size="md" className="w-full" disabled={isSubmitDisabled}>
            {isLoading ? "Загрузка..." : "Войти"}
          </Button>

          <p className="flex items-center gap-1">
            <span className="fs-200 font-medium text-neutral-400">Ещё нет аккаунта?</span>
            <Link
              to={ROUTES.register}
              className="text-primary fs-300 font-semibold hover:underline"
            >
              Регистрация
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};
